import type { TreeItem } from "./tree-item";
import type { Branch } from "./branch";

export interface Connectable<TListener> {
    connect(listener: TListener): void;
    disconnect(): void;
}

/**
 * Represents a UI element for selecting from a list of nodes under a single parent.
 * Could be implemented as a dropdown, tabs, expanding list, or any other UI pattern that allows selection of one option at a time.
 */
export interface Subselect<TValue = string> {
    /** The name of the parent node */
    name: string;
    /** The currently selected value, or null if no value under the parent node is selected. Not to be confused with the value of the parent node itself, which stays the same regardless of whether or not it is selected. */
    value: null | TValue;
    /** Resolves where a child subselect should be inserted for the current parent selection. Returns null if the subselect is terminal (not expandable) */
    getSubselectContainer(selection: TreeSelection): HTMLElement | null;
}

export interface ValueChangeListener<TSubselect extends Subselect<unknown>> {
    onValueChange(subselect: TSubselect): void;
}

export interface SingleSubselectListener extends ValueChangeListener<SingleSubselect> {}

export interface SingleSubselect extends Subselect<string>, Connectable<SingleSubselectListener> {
    onDescendantSelected?(): void;
    onAncestorSelected?(): void;
}

export interface MultiSubselect extends Subselect<string[]> {}

export interface ItemExpansionListener<TSubselect extends Subselect<unknown>> {
    onItemExpansionChange(subselect: TSubselect, item: TreeItem, expanded: boolean): void;
}

/** Represents a value being selected in the tree. Not to be confused with the overall tree select value */
export class TreeSelection<TParent extends Subselect<unknown> = Subselect<unknown>> {
    constructor(
        public readonly treeElement: HTMLElement,
        public readonly path: Branch,
        public readonly parent: TParent,
    ) {}
}

export interface TreeSelectDelegate<TSubselect extends Subselect<unknown> = SingleSubselect> {
    getSubselect(selection: TreeSelection<TSubselect>): Promise<TSubselect|null>;
    /**
     * @param onSubselectPlacement Called as soon as a subselect is placed in the DOM, so that its value can be set before any subsequent subselects are placed. This is important for tree item selects because the selected item must be known in order in order to place the subselect in the correct item's content container. 
     */
    getSubselects(root: TSubselect, value: Branch, onSubselectPlacement: (subselect: TSubselect) => void): void;
}

export interface MultiSelectionModeDelegate<TSubselect extends MultiSubselect = MultiSubselect> {
    getSubselect(selection: TreeSelection): Promise<TSubselect | null>;
}

export interface SelectionMode<TValue> {
    value: TValue;
}
